import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import TwoFactorChallenge from "@/components/app/TwoFactorChallenge";
import { useSEO } from "@/lib/seo";
import { trackLogin, trackCreateAccount, trackDealerSignup, identify } from "@/lib/analytics";

const TABS = [
  { id: "password", label: "Email + Password" },
  { id: "magic", label: "Magic Link" },
  { id: "google", label: "Google" },
];

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const initialIntent = new URLSearchParams(loc.search).get("intent") || "user";
  const [intent, setIntent] = useState(initialIntent === "investor" || initialIntent === "buyer" ? "user" : initialIntent);
  const [tab, setTab] = useState("password");

  useSEO({
    title: intent === "dealer"
      ? "Dealer Sign In — List Notes & Fund Originations | FinanceLocal"
      : "Sign In or Create a Free Account | FinanceLocal",
    description: intent === "dealer"
      ? "Dealer login for FinanceLocal. List vehicle notes, browse pre-approval applications, and fund new auto loan originations."
      : "Sign in to FinanceLocal or create a free account to buy and sell cash-flowing vehicle notes with weekly cash flow and yields up to 28%.",
    canonical: intent === "dealer"
      ? "https://myfinancelocal.com/login?intent=dealer"
      : "https://myfinancelocal.com/login",
    noIndex: false,
  });

  const intentBlock = (
    <div className="grid grid-cols-2 gap-3 mb-6">
      <button data-testid="role-user" onClick={() => setIntent("user")}
        className={`p-3 border text-left text-sm ${intent === "user" ? "border-primary bg-muted" : "border-border hover:bg-muted/40"}`}>
        <div className="font-overline text-accent">For Users</div>
        <div className="font-display text-base mt-1">Apply, buy & sell notes</div>
      </button>
      <button data-testid="role-dealer" onClick={() => setIntent("dealer")}
        className={`p-3 border text-left text-sm ${intent === "dealer" ? "border-primary bg-muted" : "border-border hover:bg-muted/40"}`}>
        <div className="font-overline text-accent">For Dealers</div>
        <div className="font-display text-base mt-1">List & buy notes</div>
      </button>
    </div>
  );

  const postLoginPath = (role) =>
    role === "admin" ? "/admin"
    : role === "dealer" ? "/dealer"
    : "/dashboard";

  const afterLogin = async (u) => {
    setUser(u);
    await checkAuth();
    navigate(postLoginPath(u.role), { replace: true });
  };

  return (
    <div data-testid="page-login" className="mx-auto max-w-xl px-4 py-16">
      <div className="border-b border-border pb-6 mb-6">
        <div className="font-overline text-accent">Welcome to FinanceLocal</div>
        <h1 className="font-display text-4xl mt-2">Sign in</h1>
        <p className="text-muted-foreground mt-2 text-sm">Choose how you'd like to sign in.</p>
      </div>

      {intentBlock}

      <div className="flex border-b border-border mb-6" data-testid="login-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs uppercase tracking-wider border-b-2 -mb-[1px] ${tab === t.id ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="border border-border p-8">
        {tab === "password" && <PasswordForm intent={intent} afterLogin={afterLogin} />}
        {tab === "magic" && <MagicForm intent={intent} />}
        {tab === "google" && <GoogleForm intent={intent} />}
      </div>

      <p className="text-[11px] text-muted-foreground mt-4 text-center">
        Having trouble with Google? Email + Password or Magic Link works without phone verification.
      </p>
    </div>
  );
}

function PasswordForm({ intent, afterLogin }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [challenge, setChallenge] = useState(null); // {twofa_token, email}
  const navigate = useNavigate();

  const submit = async () => {
    setBusy(true);
    try {
      let r;
      if (mode === "register") {
        if (password.length < 8) { toast.error("Password must be at least 8 characters"); setBusy(false); return; }
        if (!terms) { toast.error("You must accept the Terms of Service and Privacy Policy"); setBusy(false); return; }
        r = await api.post("/auth/register", { email, password, name: name || email.split("@")[0], intended_role: intent, terms_accepted: terms });
      } else {
        r = await api.post("/auth/login", { email, password });
      }
      if (r.data?.requires_2fa) {
        setChallenge({ twofa_token: r.data.twofa_token, email: r.data.email || email });
        setBusy(false);
        return;
      }
      // Analytics: identify user, then fire the appropriate conversion event.
      identify(r.data?.user_id || r.data?.id, { role: r.data?.role });
      if (mode === "register") {
        trackCreateAccount("password", r.data?.role || intent);
        if ((r.data?.role || intent) === "dealer") trackDealerSignup("password");
      } else {
        trackLogin("password", r.data?.role);
      }
      toast.success(mode === "register" ? "Welcome — account created" : "Signed in");
      await afterLogin(r.data);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (challenge) {
    return (
      <TwoFactorChallenge
        twofa_token={challenge.twofa_token}
        email={challenge.email}
        onVerified={async (u) => { toast.success("Signed in"); await afterLogin(u); }}
        onCancel={() => setChallenge(null)}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="pwd-form">
      <div className="flex border border-border">
        {["login", "register"].map((m) => (
          <button key={m} data-testid={`mode-${m}`} onClick={() => setMode(m)}
            className={`flex-1 py-2 text-xs uppercase tracking-wider ${mode === m ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>
            {m === "login" ? "Sign In" : "Create Account"}
          </button>
        ))}
      </div>
      {mode === "register" && (
        <div>
          <Label className="font-overline">Name</Label>
          <Input className="rounded-none mt-2" value={name} onChange={(e) => setName(e.target.value)} data-testid="pwd-name" />
        </div>
      )}
      <div>
        <Label className="font-overline">Email</Label>
        <Input type="email" className="rounded-none mt-2" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pwd-email" />
      </div>
      <div>
        <Label className="font-overline">Password</Label>
        <Input type="password" className="rounded-none mt-2" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="pwd-password" />
      </div>
      {mode === "register" && (
        <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="terms-checkbox-label">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
            className="mt-0.5" data-testid="terms-checkbox" />
          <span>
            I agree to the <a href="/legal?t=terms" target="_blank" rel="noreferrer" className="underline">Terms of Service</a>{" "}
            and <a href="/legal?t=privacy" target="_blank" rel="noreferrer" className="underline">Privacy Policy</a>.
          </span>
        </label>
      )}
      <Button onClick={submit} disabled={busy || !email || !password || (mode === "register" && !terms)}
        className="w-full rounded-none uppercase tracking-wider h-11" data-testid="pwd-submit">
        {busy ? "…" : mode === "register" ? "Create Account" : "Sign In"}
      </Button>
      {mode === "login" && (
        <button onClick={() => navigate("/forgot-password")} className="text-xs text-muted-foreground hover:text-primary" data-testid="forgot-link">
          Forgot password?
        </button>
      )}
    </div>
  );
}

function MagicForm({ intent }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/magic-link/request", { email, intended_role: intent });
      setSent(r.data);
      toast.success(r.data.dev_magic_link ? "Use the dev link below" : "Magic link sent to your email");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="text-center space-y-4" data-testid="magic-sent">
        <div className="font-display text-2xl">Check your email</div>
        <p className="text-muted-foreground text-sm">We sent a sign-in link to <b>{email}</b>. It expires in 15 minutes.</p>
        {sent.dev_magic_link && (
          <div className="border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs">
            <div className="font-semibold mb-1">Dev mode (Resend not configured):</div>
            <a href={sent.dev_magic_link} className="underline break-all" data-testid="dev-magic-link">{sent.dev_magic_link}</a>
          </div>
        )}
        <Button onClick={() => setSent(null)} variant="outline" className="rounded-none uppercase text-xs tracking-wider">
          Send to a different email
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="magic-form">
      <p className="text-sm text-muted-foreground">We'll email you a one-tap sign-in link. No password needed.</p>
      <div>
        <Label className="font-overline">Email</Label>
        <Input type="email" className="rounded-none mt-2" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="magic-email" />
      </div>
      <Button onClick={submit} disabled={busy || !email}
        className="w-full rounded-none uppercase tracking-wider h-11" data-testid="magic-submit">
        {busy ? "…" : "Send Magic Link"}
      </Button>
    </div>
  );
}

function GoogleForm({ intent }) {
  const handle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const dest = intent === "dealer" ? "/dealer" : "/dashboard";
    sessionStorage.setItem("fl_intended_role", intent);
    sessionStorage.setItem("fl_post_login", dest);
    // Fire the acquisition event before we redirect off-domain so it counts.
    trackLogin("google", intent);
    if (intent === "dealer") trackDealerSignup("google");
    const redirectUrl = window.location.origin + dest;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Continue with your Google account.</p>
      <p className="text-[11px] text-muted-foreground border border-border p-3 bg-muted/30">
        Note: Google may require a one-time device verification on your phone. If that's blocking you, switch to Email + Password above.
      </p>
      <Button onClick={handle} data-testid="google-login-button"
        className="w-full rounded-none uppercase tracking-wider h-11">
        Continue with Google as {intent === "dealer" ? "Dealer" : "User"}
      </Button>
    </div>
  );
}
